/**
 * Seva Mandir V1 rules for the processing phone (alignment map v1-F6.json), on top of the
 * full-spec rules in ../model/rules.ts. PURE: every function here is unit-tested.
 *
 * What V1 changes on the phone:
 *  - one pick list of machine × step (labels are bound on their own screen, v1/MachineLabel.tsx);
 *  - no flush, and the run does not wait on an issue task (bags are given on v1/Issue.tsx): Close
 *    run waits only for the cleanout, a bag in, and every output tagged or marked "nothing came
 *    out";
 *  - output tags and QC seals from any roll of this site (not only the operator's own roll);
 *  - the run's kilos are shown only after the close.
 */
import type { Grams } from '@/lib/weight'
import type { Step, StepOutput } from '@/offline/sitePack'
import {
  isRunLive,
  liveOutputs,
  type MachineView,
  type ProcessState,
  type RunView,
} from '../projection'
import { checkFreshCode, rollOf, runFigures, type Verdict } from '../model/rules'
import { ratioBp } from '../model/balance'

const norm = (c: string) => c.trim().toUpperCase()

// ---------------------------------------------------------------------------------------------
// F6.1 — the machine pick list
// ---------------------------------------------------------------------------------------------

export interface MachineChoice {
  machine: MachineView
  step_code: string
  /** A run already open on this machine (the row leads to it). */
  busy: RunView | null
}

/** One row per machine and step, in the site list's order. DELABEL is the pack hall's. */
export function machineChoices(state: ProcessState): MachineChoice[] {
  const out: MachineChoice[] = []
  for (const m of state.machines.values()) {
    const busy =
      [...state.runs.values()].find((r) => r.equipment_id === m.id && isRunLive(r.status)) ?? null
    for (const s of m.step_codes) if (s !== 'DELABEL') out.push({ machine: m, step_code: s, busy })
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// Typed numbers
// ---------------------------------------------------------------------------------------------

/**
 * "नंबर लिखें": the digits printed under a code. A bag tag is found among the site's bags by its
 * last part ("3188" → "RJ-BELU-3188"), a machine label among the site's bound machine labels;
 * an output tag or seal on a roll of this site of that kind
 * (with the roll's zero padding when it says so). Anything else is returned upper-cased.
 */
export function expandTyped(
  state: ProcessState,
  raw: string,
  kind: 'BAG' | 'OUTPUT_BAG' | 'QC_SEAL' | 'MACHINE',
): string {
  const c = norm(raw)
  if (!/^\d+$/.test(c)) return c
  const n = Number(c)
  if (kind === 'MACHINE') {
    // A bound machine label of this site ending in the number, else a MACHINE roll's code.
    const hits = [...state.machineByLabel.keys()].filter((p) => {
      const tail = /(\d+)$/.exec(p)?.[1]
      return tail !== undefined && Number(tail) === n
    })
    if (hits.length === 1) return hits[0]!
  }
  if (kind === 'BAG') {
    // The number the tag ends in (RJ-VDEMO-SA155 → 155): rolls put letters before it.
    const hits = [...state.bags.values()].filter((b) => {
      const tail = /(\d+)$/.exec(b.qr_payload)?.[1]
      return tail !== undefined && Number(tail) === n
    })
    return hits.length === 1 ? norm(hits[0]!.qr_payload) : c
  }
  for (const r of state.pack.rolls) {
    if (r.kind !== kind || n < r.start_sequence || n > r.end_sequence) continue
    // The roll's zero padding: as the server says, else as a code already seen from this roll.
    const seen = [...r.used, ...state.usedTags].find((u) =>
      norm(u).startsWith(norm(r.payload_prefix)),
    )
    const digits =
      typeof r.sequence_digits === 'number'
        ? r.sequence_digits
        : seen
          ? seen.length - r.payload_prefix.length
          : c.length
    const code = norm(`${r.payload_prefix}${String(n).padStart(digits, '0')}`)
    if (rollOf([r], code)) return code
  }
  return c
}

// ---------------------------------------------------------------------------------------------
// Tags and seals: any roll of the site
// ---------------------------------------------------------------------------------------------

/**
 * A fresh output tag or QC seal. V1: rolls belong to the site, so a roll issued to someone else
 * here is accepted (the scan still records who used it).
 */
export function freshCodeV1(
  state: ProcessState,
  code: string,
  kind: 'OUTPUT_BAG' | 'QC_SEAL',
  actorId: string,
  alsoUsed: ReadonlySet<string> = new Set(),
): Verdict<string> {
  const v = checkFreshCode(state, code, kind, actorId, alsoUsed)
  if (!v.ok && v.reason === 'NOT_YOUR_ROLL') {
    const c = norm(code)
    if (state.usedTags.has(c) || alsoUsed.has(c)) return { ok: false, reason: 'TAG_USED' }
    return { ok: true, value: c }
  }
  return v
}

// ---------------------------------------------------------------------------------------------
// F6.5 — outputs and close
// ---------------------------------------------------------------------------------------------

/** Waste and sweepings go on the bench scale (red tie); everything else on the platform scale. */
export const onBenchScale = (o: Pick<StepOutput, 'unit_type'>) => o.unit_type === 'REJECT'

/** Live sacks of one output. */
export function sacksOf(run: RunView, key: string) {
  return liveOutputs(run).filter((o) => o.stream_key === key)
}

export type CloseGapV1 =
  { kind: 'NO_CLEANOUT' } | { kind: 'NO_INPUT' } | { kind: 'OUTPUT_MISSING'; key: string }

/**
 * What still stops Close run in V1: the cleanout, at least one bag in, and every output either
 * tagged or marked "nothing came out". No flush, no issued-bags check, no Machine stopped.
 */
export function closeGapsV1(
  run: RunView,
  step: Step | undefined,
  zero: readonly string[],
): CloseGapV1[] {
  const gaps: CloseGapV1[] = []
  if (!run.cleanout_confirmed_at) gaps.push({ kind: 'NO_CLEANOUT' })
  if (run.inputs.length === 0) gaps.push({ kind: 'NO_INPUT' })
  for (const o of step?.outputs ?? [])
    if (!zero.includes(o.key) && sacksOf(run, o.key).length === 0)
      gaps.push({ kind: 'OUTPUT_MISSING', key: o.key })
  return gaps
}

/** Every output has a sack or a "nothing came out". */
export const outputsDone = (run: RunView, step: Step | undefined, zero: readonly string[]) =>
  closeGapsV1(run, step, zero).every((g) => g.kind !== 'OUTPUT_MISSING')

export interface CloseResult {
  input_g: Grams
  output_g: Grams
  /** Kilos in less kilos out (positive = less came out). */
  residual_g: Grams
  kind: 'LESS' | 'MORE' | 'ZERO'
  /** Beyond the step's loss limit (either way). */
  beyond: boolean
  /** The office looks at it: a loss beyond the limit (a note), more out than in beyond the limit or exactly zero (held). */
  office: boolean
}

/**
 * The kilos shown after the close (the phone's own figures until the server's balance arrives).
 * V1 has no yield range: only the loss limit and exact zero are checked.
 */
export function closeResult(run: RunView, step: Step | undefined): CloseResult {
  const balance = run.balance
  const fig = runFigures(run, step)
  const input_g = balance?.input_g ?? fig.input_g
  const output_g = balance?.output_g ?? fig.flush_g + fig.product_g + fig.byproduct_g + fig.reject_g
  const residual_g = input_g - output_g
  const limitBp = Math.round((step?.loss_limit_pct ?? 0.5) * 100)
  const bp = input_g > 0 ? Math.abs(ratioBp(residual_g, input_g)) : 0
  const kind = residual_g === 0 ? 'ZERO' : residual_g > 0 ? 'LESS' : 'MORE'
  const beyond = bp > limitBp
  return { input_g, output_g, residual_g, kind, beyond, office: kind === 'ZERO' || beyond }
}

// ---------------------------------------------------------------------------------------------
// F6-B1.b — the cause, in three pictures then the detail
// ---------------------------------------------------------------------------------------------

export const CAUSE_GROUPS = {
  WEIGHT: ['UNWEIGHED_SACK', 'SCALE_ERROR', 'TYPING_ERROR', 'BAG_IN_WITHOUT_SCAN'],
  MACHINE: ['HIGH_MOISTURE', 'MACHINE_FAULT'],
  MIXING: ['MIXING'],
  UNKNOWN: ['UNEXPLAINED_LOSS'],
} as const

export type CauseGroup = keyof typeof CAUSE_GROUPS
export type CauseV1 = (typeof CAUSE_GROUPS)[CauseGroup][number]

/** The group a cause sits in. */
export function causeGroup(cause: string | null): CauseGroup | null {
  for (const [g, list] of Object.entries(CAUSE_GROUPS) as [CauseGroup, readonly string[]][])
    if (cause && list.includes(cause)) return g
  return null
}

// ---------------------------------------------------------------------------------------------
// F6-B1.c — the supervisor decides a held run on the phone
// ---------------------------------------------------------------------------------------------

/** The run's open balance problem (what a decision closes). */
export function balanceProblem(
  run: Pick<RunView, 'open_exceptions'>,
): RunView['open_exceptions'][number] | null {
  return run.open_exceptions.find((e) => e.type === 'BALANCE_OUT_OF_BAND') ?? null
}

/**
 * Does a run balance with `short_g` missing (negative: a surplus)? Within the step's loss limit
 * either way. The server re-checks; this only decides whether to ask it to close the problem.
 */
export function withinLossLimit(input_g: Grams, short_g: Grams, lossLimitPct: number): boolean {
  if (input_g <= 0) return false
  return (Math.abs(short_g) * 100) / input_g <= lossLimitPct
}

/** Problem kinds a loss may be accepted for (the server's canAcceptShortfall). */
const ACCEPTABLE_KINDS: readonly string[] = ['RESIDUAL', 'YIELD', 'EXACT_ZERO']

/**
 * Can the supervisor accept the shortfall on the phone? Only when less came out than went in
 * (`short_g` > 0), the problem is a loss or yield one and the cause is not mixing. A surplus is
 * never accepted: the sacks are corrected instead.
 */
export function canAcceptShortfall(
  kinds: readonly string[],
  short_g: Grams | null,
  cause: string | null,
): boolean {
  if (!cause || cause === 'MIXING') return false
  if (short_g === null || short_g <= 0) return false
  return kinds.every((k) => ACCEPTABLE_KINDS.includes(k))
}
